import { useEffect, useState } from "react";
import { Trash2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Nav } from "@/components/Nav";
import { VerdictPanel } from "@/components/VerdictPanel";
import { api } from "@/lib/api";

const BADGE = {
  correct: "bg-emerald-100 text-emerald-900",
  incorrect: "bg-red-100 text-red-900",
  partially_correct: "bg-amber-100 text-amber-900",
  suggested: "bg-[#0f172a] text-white",
};

const PAGE = 10;

export default function History() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [active, setActive] = useState(null);

  const fetchPage = async (offset, limit = PAGE) => {
    const { data } = await api.get("/analyses", { params: { limit, offset } });
    setTotal(data.total);
    setHasMore(data.has_more);
    return data.items;
  };

  useEffect(() => {
    (async () => {
      try {
        const first = await fetchPage(0);
        setItems(first);
        setActive(first[0] || null);
      } catch {
        toast.error("Could not load history");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const loadMore = async () => {
    setLoadingMore(true);
    try {
      const next = await fetchPage(items.length);
      setItems((prev) => [...prev, ...next]);
    } catch {
      toast.error("Could not load more");
    } finally {
      setLoadingMore(false);
    }
  };

  const remove = async (id) => {
    await api.delete(`/analyses/${id}`);
    const kept = items.filter((i) => i.analysis_id !== id);
    try {
      const fresh = await fetchPage(0, Math.max(PAGE, kept.length));
      setItems(fresh);
      setActive((a) => (a?.analysis_id === id ? fresh[0] || null : a));
    } catch {
      setItems(kept);
      if (active?.analysis_id === id) setActive(kept[0] || null);
    }
    toast.success("Deleted");
  };

  return (
    <div className="grain min-h-screen">
      <Nav />
      <main className="mx-auto max-w-[1400px] px-6 py-14 lg:px-12">
        <p className="overline text-slate-500">Saved reviews</p>
        <h1 className="font-display mt-4 text-4xl font-bold tracking-tight sm:text-5xl">History</h1>

        {loading ? (
          <div className="mt-16 flex items-center gap-2 text-slate-500"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>
        ) : items.length === 0 ? (
          <p className="mt-12 text-sm text-slate-500" data-testid="history-empty">No saved analyses yet. Analyze an entry while signed in and it will appear here.</p>
        ) : (
          <>
            <p className="mt-4 font-mono-data text-xs text-slate-500" data-testid="history-count">
              Showing {items.length} of {total}
            </p>
            <div className="mt-10 grid gap-10 lg:grid-cols-[1fr_1fr]">
              <div>
                <div className="divide-y divide-stone-200 border border-stone-200 bg-white" data-testid="history-list">
                  {items.map((it) => (
                    <div key={it.analysis_id} onClick={() => setActive(it)} data-testid={`history-item-${it.analysis_id}`}
                      className={`cursor-pointer p-5 transition-colors duration-150 hover:bg-stone-50 ${active?.analysis_id === it.analysis_id ? "bg-stone-50" : ""}`}>
                      <div className="flex items-start justify-between gap-4">
                        <div className="min-w-0">
                          <span className={`overline inline-block px-2 py-0.5 ${BADGE[it.verdict] || BADGE.partially_correct}`}>{it.verdict.replace("_", " ")}</span>
                          <p className="mt-2 truncate font-mono-data text-xs text-slate-600">{it.input_summary?.split("\n")[0]}</p>
                          <p className="mt-1 font-mono-data text-[11px] text-slate-400">
                            {it.framework === "indas" ? "Ind AS" : "AS"} · {new Date(it.created_at).toLocaleString("en-IN")}
                          </p>
                        </div>
                        <button onClick={(e) => { e.stopPropagation(); remove(it.analysis_id); }} data-testid={`delete-${it.analysis_id}`}
                          className="p-1.5 text-slate-400 transition-colors duration-150 hover:text-red-700">
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
                {hasMore && (
                  <button onClick={loadMore} disabled={loadingMore} data-testid="load-more-btn"
                    className="mt-4 flex w-full items-center justify-center gap-2 border border-stone-300 px-6 py-3 text-sm text-slate-600 transition-colors duration-150 hover:border-[#0f172a] hover:text-[#0f172a] disabled:opacity-60">
                    {loadingMore ? <><Loader2 className="h-4 w-4 animate-spin" /> Loading…</> : "Load more"}
                  </button>
                )}
              </div>
              <div className="lg:sticky lg:top-24 lg:self-start">
                {active && <VerdictPanel result={active} />}
              </div>
            </div>
          </>
        )}
      </main>
    </div>
  );
}
